import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  createAnalysisEntry,
  readAnalysisEntry,
  type AnalysisCtaId,
  type AnalysisLanding,
  type AnalysisLocale,
} from "@appcaire/shared/analysis/analysisEntry";
import {
  ANALYSIS_SIGNUP_TARGET,
  analysisAppSignupHref,
  analysisLandingForPath,
  analysisOfferHref,
  analysisOfferLabel,
  captureAnalysisEntry,
} from "@/config/analysisOffer";
import { trackMarketingCta } from "@/lib/marketingCta";

export const analysisButtonClass =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-coral-text px-5 py-3 text-sm font-semibold text-coral-fg transition hover:bg-coral-action-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 motion-reduce:transition-none";

type Props = {
  locale: AnalysisLocale;
  landing?: AnalysisLanding;
  cta?: AnalysisCtaId;
  start?: boolean;
  className?: string;
  onClick?: () => void;
  label?: string;
};

/** Every public analysis CTA shares a destination and exactly one click event. */
export function AnalysisCta({
  locale,
  landing,
  cta = "analysis_contextual",
  start = false,
  className = analysisButtonClass,
  onClick,
  label: customLabel,
}: Props) {
  const location = useLocation();
  const page = landing ?? analysisLandingForPath(location.pathname);
  const [context, setContext] = useState(
    () =>
      (start ? readAnalysisEntry(location.search) : null) ??
      createAnalysisEntry(locale, page, cta, location.search),
  );
  useEffect(() => {
    const incoming = start ? readAnalysisEntry(location.search) : null;
    setContext(
      incoming
        ? { ...incoming, locale }
        : captureAnalysisEntry(locale, page, cta, location.search),
    );
  }, [locale, page, cta, location.search, start]);
  const href = start
    ? analysisAppSignupHref(context)
    : analysisOfferHref(context);
  const label =
    customLabel ??
    (start
      ? locale === "sv"
        ? "Analysera er besöksplanering kostnadsfritt"
        : "Start your complimentary analysis"
      : analysisOfferLabel(locale));
  const handleClick = () => {
    trackMarketingCta(
      cta,
      label,
      start ? ANALYSIS_SIGNUP_TARGET : href.split("?")[0],
      context,
    );
    onClick?.();
  };
  const content = (
    <>
      {label}
      <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
    </>
  );
  return start ? (
    <a
      href={href}
      className={className}
      data-cta-id={cta}
      onClick={handleClick}
    >
      {content}
    </a>
  ) : (
    <Link
      to={href}
      className={className}
      data-cta-id={cta}
      onClick={handleClick}
    >
      {content}
    </Link>
  );
}
